// SEQ-180 phase 1 client-side rollup.
//
// SEQ-193 UPDATE: the RPC has a combined project+month grain again
// (groupBy = 'project_month', migration 20261006000100). It is used ONLY by
// the Project performance table's By month view, through projectMonthTable.ts,
// which reads those rows cell by cell and takes each project's Total from the
// project grain. Nothing here sums them. The paragraphs below describe the
// older contract and still hold for every other caller.
//
// CONTRACT CHANGE (round 3 addendum): the RPC's groupBy vocabulary no longer
// includes a combined project+month grain. `pnl_analytics_query` only
// accepts project | month | quarter | calendar_year | financial_year |
// builder | status (the v_group_by CHECK, since widened by
// 20260910000300_pnl_analytics_query_v3.sql to add company, project_manager
// and site_manager). Each accepted value sums a bucket server-side, so a
// returned row's dollar metrics are already correct for its own grain and
// this module never re-derives them.
//
// WAGES MULTIPLIER (14 September 2026 grain fix, superseding this header's
// earlier claim that the multiplier is "already applied per project per
// month" for every grain): the RPC applies it only to buckets that span more
// than one calendar month. A groupBy = 'month' row therefore carries RAW
// wages, matching a project P&L tab month cell, while every aggregate grain
// carries multiplied wages, matching the tab's Total column. See
// src/lib/pnl/wagesMultiplierGrain.ts.
//
// What that means for THIS module: never build a total by summing month-grain
// rows. sumRollups is fed project-grain rollups only (widgetDataset.ts's
// companyTotals, and foldToCompanyBuckets, which guards on projectGrainRows),
// and it must stay that way. Summing month rows would produce a total that
// silently omits the multiplier and disagrees with every other total on the
// page.
// This module therefore does two things, not three:
//
// 1. Map one grain's rows straight into a typed rollup (metricsToRollup),
//    no arithmetic beyond reading keys out of the metrics map and deriving
//    this page's ratios from them.
// 2. Sum several rows' dollar totals into one wider total (sumRollups), for
//    the KPI cards' company figure, built from the same `project`-grain rows
//    already fetched for the bar chart, scatters and table. Ratios here are
//    still sum-then-divide, never an average of the rows' own ratios,
//    matching plan section 6.5 and this module's own `ratio()` below. A zero
//    denominator resolves to null so a chart can omit the point instead of
//    plotting a misleading zero.
//
// Getting the trend line's "one selected project" view therefore needs its
// own targeted `groupBy: 'month', projectIds: [id]` call (see
// usePnlAnalyticsForProject in usePnlAnalytics.ts) rather than a client-side
// filter of data already on the page, because no response on the page ever
// carries both a projectId and a periodMonth on the same row any more.
//
// SEQ-182: ordinaryHours/overtimeHours are requested as raw leaf metrics
// (labour.ordinary_hours, labour.overtime_hours), exactly like every other
// figure this module rolls up. totalLabourHours, overtimeHoursPct and
// revenuePerLabourHour are derived client side from those leaf metrics,
// sum-then-divide, the same discipline profitPercent/overtimePctRevenue
// already use. This deliberately does NOT request
// calculated.total_labour_hours / calculated.overtime_hours_pct from the
// RPC: the established pattern on this page is "fetch inputs, derive
// ratios here", never "fetch a pre-derived ratio and re-aggregate it",
// which is exactly the AC10 mistake ("do not sum incompatible units" also
// means "do not average a percentage across grains and call it correct").
//
// SEQ-182 round 2 (browser E2E): overtime (dollars), ordinaryHours and
// overtimeHours are payroll-source figures. The RPC returns null for all
// three on a project with no mapped payroll site, meaning "unknown", not
// "zero". Coalescing them to 0 here made an unmapped project look like a
// project that genuinely worked zero hours: the analytics table showed
// "0.0 hrs" instead of the P&L tab band's correct blank state
// (PnlManualMetricRows.tsx / operationalHoursTotals.ts already preserve null
// through that separate data path). This module now preserves null for
// every payroll-source key end to end: a single row's null metric stays
// null in its rollup, and `sumRollups` sums only the rows that actually
// have a value, resolving to null only when every rollup being summed is
// null (never a 0 that reads as "no overtime"). revenue/cost/profit/patching
// are ordinary P&L category figures, not payroll-derived, so they keep the
// existing "missing means 0" behaviour unchanged.
//
// SEQ-182 round 3 (evidence-matrix gap): overtimePctWages, labourCostPerHour
// and profitPerLabourHour are the RPC's own already-computed ratios
// (calculated.overtime_pct_wages / labour_cost_per_hour /
// profit_per_labour_hour), read straight through per project row rather
// than re-derived here. This is deliberately NOT the same pattern as
// profitPercent/overtimeHoursPct/revenuePerLabourHour above: those are
// derived from raw inputs THIS module already fetches (revenue, cost,
// hours), so sum-then-divide across a company total is correct and cheap.
// These three would need wages_total / loaded-labour-cost totals this
// module does not fetch to recompute correctly at company grain, and
// summing or averaging the already-computed per-project ratios instead
// would be exactly the AC10 mistake this file's own header warns against.
// So they are read per project row (`metricsToRollup`) but always null at
// company grain (`sumRollups`, via `finalize`'s own default): a table
// column and a scatter tooltip only ever show them per project, never
// summed into the KPI cards.

import type { PnlAnalyticsRow } from "../../lib/pnl/analyticsClient";
import { PNL_METRIC_KEYS } from "../../lib/pnl/pnlAnalytics";

export interface PnlRollup {
  revenue: number;
  cost: number;
  profit: number;
  profitPercent: number | null;
  /** SEQ-182 round 2. Payroll-source (KeyPay-derived). Null means "no payroll data for this selection", never 0. */
  overtime: number | null;
  /** Null whenever `overtime` is null: an unknown dollar figure has no percent-of-revenue either. */
  overtimePctRevenue: number | null;
  patching: number;
  patchingPctRevenue: number | null;
  /** AC05: patching as a percent of total cost, not just revenue. */
  patchingPctCost: number | null;
  /** SEQ-182. Hours from payroll, requested as leaf metrics (never typed). Payroll-source: null means unmapped/no data, never 0. */
  ordinaryHours: number | null;
  overtimeHours: number | null;
  /** SEQ-182. ordinaryHours + overtimeHours, recomputed at every grain rather than fetched pre-summed. Null only when both components are null. */
  totalLabourHours: number | null;
  /** SEQ-182. overtimeHours / totalLabourHours x 100. Null when totalLabourHours is null or 0. */
  overtimeHoursPct: number | null;
  /** SEQ-182. revenue / totalLabourHours. Null when totalLabourHours is null or 0. */
  revenuePerLabourHour: number | null;
  /** SEQ-182 round 3. calculated.overtime_pct_wages, read straight from the RPC per project row. Null when the project has no payroll mapping, or at company grain (see file header). */
  overtimePctWages: number | null;
  /** SEQ-182 round 3. calculated.labour_cost_per_hour, read straight from the RPC per project row. Same null rule as overtimePctWages. */
  labourCostPerHour: number | null;
  /** SEQ-182 round 3. calculated.profit_per_labour_hour, read straight from the RPC per project row. Same null rule as overtimePctWages. */
  profitPerLabourHour: number | null;
}

export interface ProjectRollup extends PnlRollup {
  projectId: string;
}

export interface MonthRollup extends PnlRollup {
  periodMonth: string;
}

/** The metric keys every RPC call this page makes needs to request. */
export const PNL_ROLLUP_METRICS: string[] = [
  PNL_METRIC_KEYS.calculated.totalIncome,
  PNL_METRIC_KEYS.calculated.totalCost,
  PNL_METRIC_KEYS.calculated.netProfit,
  PNL_METRIC_KEYS.labour.overtime,
  PNL_METRIC_KEYS.expense.patching,
  PNL_METRIC_KEYS.labour.ordinaryHours,
  PNL_METRIC_KEYS.labour.overtimeHours,
  PNL_METRIC_KEYS.calculated.overtimePctWages,
  PNL_METRIC_KEYS.calculated.labourCostPerHour,
  PNL_METRIC_KEYS.calculated.profitPerLabourHour,
];

interface RawTotals {
  revenue: number;
  cost: number;
  profit: number;
  overtime: number | null;
  patching: number;
  ordinaryHours: number | null;
  overtimeHours: number | null;
}

function ratio(numerator: number, denominator: number): number | null {
  if (!(denominator > 0)) return null;
  return (numerator / denominator) * 100;
}

/** Like `ratio`, but without the x100: for a currency-per-hour rate, not a percent. */
function rate(numerator: number, denominator: number): number | null {
  if (!(denominator > 0)) return null;
  return numerator / denominator;
}

/**
 * Adds two nullable payroll figures, preserving "unknown" correctly: null
 * only when BOTH inputs are null (nothing to add), otherwise a null input
 * contributes 0 (matching operationalHoursTotals.ts's identical rule for
 * the P&L tab band, so the two data paths agree).
 */
function sumNullable(a: number | null, b: number | null): number | null {
  if (a === null && b === null) return null;
  return (a ?? 0) + (b ?? 0);
}

function rawTotalsOf(metrics: Record<string, number | null>): RawTotals {
  return {
    revenue: metrics[PNL_METRIC_KEYS.calculated.totalIncome] ?? 0,
    cost: metrics[PNL_METRIC_KEYS.calculated.totalCost] ?? 0,
    profit: metrics[PNL_METRIC_KEYS.calculated.netProfit] ?? 0,
    // Payroll-source: ?? null, not ?? 0. A missing/null key means "unknown",
    // exactly like a missing month in operationalHoursTotals.ts.
    overtime: metrics[PNL_METRIC_KEYS.labour.overtime] ?? null,
    patching: metrics[PNL_METRIC_KEYS.expense.patching] ?? 0,
    ordinaryHours: metrics[PNL_METRIC_KEYS.labour.ordinaryHours] ?? null,
    overtimeHours: metrics[PNL_METRIC_KEYS.labour.overtimeHours] ?? null,
  };
}

function finalize(totals: RawTotals): PnlRollup {
  const totalLabourHours = sumNullable(totals.ordinaryHours, totals.overtimeHours);
  return {
    revenue: totals.revenue,
    cost: totals.cost,
    profit: totals.profit,
    profitPercent: ratio(totals.profit, totals.revenue),
    overtime: totals.overtime,
    overtimePctRevenue: totals.overtime === null ? null : ratio(totals.overtime, totals.revenue),
    patching: totals.patching,
    patchingPctRevenue: ratio(totals.patching, totals.revenue),
    patchingPctCost: ratio(totals.patching, totals.cost),
    ordinaryHours: totals.ordinaryHours,
    overtimeHours: totals.overtimeHours,
    totalLabourHours,
    overtimeHoursPct: totalLabourHours === null ? null : ratio(totals.overtimeHours ?? 0, totalLabourHours),
    revenuePerLabourHour: totalLabourHours === null ? null : rate(totals.revenue, totalLabourHours),
    // Default null. `metricsToRollup` overrides these with the RPC's own
    // per-row values; `sumRollups` deliberately leaves this default in
    // place (see file header, SEQ-182 round 3).
    overtimePctWages: null,
    labourCostPerHour: null,
    profitPerLabourHour: null,
  };
}

/** Maps one RPC row's metrics straight into a rollup, at whatever grain that row already is. */
export function metricsToRollup(metrics: Record<string, number | null>): PnlRollup {
  return {
    ...finalize(rawTotalsOf(metrics)),
    // SEQ-182 round 3: read straight through, per row, never re-derived
    // (see file header for why these three differ from every other field
    // this function computes).
    overtimePctWages: metrics[PNL_METRIC_KEYS.calculated.overtimePctWages] ?? null,
    labourCostPerHour: metrics[PNL_METRIC_KEYS.calculated.labourCostPerHour] ?? null,
    profitPerLabourHour: metrics[PNL_METRIC_KEYS.calculated.profitPerLabourHour] ?? null,
  };
}

/** project-grain rows (groupBy: "project") to one rollup per project. */
export function projectRowsToRollups(rows: PnlAnalyticsRow[]): ProjectRollup[] {
  return rows
    .filter((row): row is PnlAnalyticsRow & { projectId: string } => row.projectId !== null)
    .map((row) => ({ projectId: row.projectId, ...metricsToRollup(row.metrics) }));
}

/** month-grain rows (groupBy: "month") to one rollup per calendar month, sorted ascending. */
export function monthRowsToRollups(rows: PnlAnalyticsRow[]): MonthRollup[] {
  return rows
    .filter((row): row is PnlAnalyticsRow & { periodMonth: string } => row.periodMonth !== null)
    .map((row) => ({ periodMonth: row.periodMonth, ...metricsToRollup(row.metrics) }))
    .sort((a, b) => a.periodMonth.localeCompare(b.periodMonth));
}

/**
 * Sums several rollups' dollar totals into one wider rollup, recomputing
 * every ratio sum-then-divide. Payroll-source fields sum only the rollups
 * that have a value (`sumNullable`): a company total with one unmapped
 * project among several mapped ones is the sum of the mapped ones; a
 * company total where every project is unmapped is null, never 0.
 */
export function sumRollups(rollups: PnlRollup[]): PnlRollup {
  const totals = rollups.reduce<RawTotals>(
    (acc, r) => ({
      revenue: acc.revenue + r.revenue,
      cost: acc.cost + r.cost,
      profit: acc.profit + r.profit,
      overtime: sumNullable(acc.overtime, r.overtime),
      patching: acc.patching + r.patching,
      ordinaryHours: sumNullable(acc.ordinaryHours, r.ordinaryHours),
      overtimeHours: sumNullable(acc.overtimeHours, r.overtimeHours),
    }),
    { revenue: 0, cost: 0, profit: 0, overtime: null, patching: 0, ordinaryHours: null, overtimeHours: null },
  );
  return finalize(totals);
}

// SEQ-232. A rollup with some cells taken back out of it: `total` minus the
// sum of `removed`, every ratio recomputed sum-then-divide from the remaining
// dollars and hours (never subtracted as percentages).
//
// Used for ONE job: taking the project-months with no costs entered out of a
// project's Total. The Total comes from the project grain (multiplied wages),
// so it cannot be rebuilt by summing month cells. Subtracting a month cell is
// exact for that cell's own figures: a project-month with no source=manual row
// has no manual wages for the multiplier to act on, so its raw cell and its
// share of the multiplied Total are the same dollars. This holds by construction:
// wages exist only as manual rows, so a month with no manual row has wages of 0,
// the wages multiplier adds nothing to it, and subtracting its raw cells from the
// multiplied Total is exact.
//
// The three ratios the RPC computes per row (overtimePctWages,
// labourCostPerHour, profitPerLabourHour) cannot be recomputed here, for the
// same reason sumRollups leaves them null. They go null on an adjusted Total
// rather than showing a figure for the unadjusted span. Payroll-source figures
// keep their rule: null stays null, and a null removed cell takes nothing out.
export function differenceRollup(total: PnlRollup, removed: PnlRollup[]): PnlRollup {
  if (removed.length === 0) return total;
  const gone = removed.reduce<RawTotals>(
    (acc, r) => ({
      revenue: acc.revenue + r.revenue,
      cost: acc.cost + r.cost,
      profit: acc.profit + r.profit,
      overtime: sumNullable(acc.overtime, r.overtime),
      patching: acc.patching + r.patching,
      ordinaryHours: sumNullable(acc.ordinaryHours, r.ordinaryHours),
      overtimeHours: sumNullable(acc.overtimeHours, r.overtimeHours),
    }),
    { revenue: 0, cost: 0, profit: 0, overtime: null, patching: 0, ordinaryHours: null, overtimeHours: null },
  );
  // Rounded to the cent (hours to 0.01) so removing every cell leaves exactly 0, not 1e-12: a sliver of
  // revenue would otherwise turn into an absurd percentage, because ratio() only guards a zero denominator.
  const round = (n: number): number => Math.round(n * 100) / 100;
  const minus = (a: number | null, b: number | null): number | null => (a === null ? null : round(a - (b ?? 0)));
  return finalize({
    revenue: round(total.revenue - gone.revenue),
    cost: round(total.cost - gone.cost),
    profit: round(total.profit - gone.profit),
    overtime: minus(total.overtime, gone.overtime),
    patching: round(total.patching - gone.patching),
    ordinaryHours: minus(total.ordinaryHours, gone.ordinaryHours),
    overtimeHours: minus(total.overtimeHours, gone.overtimeHours),
  });
}
